import React, { useState, useEffect } from "react";
import "./NewSport.css";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import Swal from 'sweetalert2';
import axios from "axios";

const NewSport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('auth-token');
  const queryParams = new URLSearchParams(location.search);
  const action = queryParams.get("action");  
  const sportId = queryParams.get("sportId");  
  const [imagePreview, setImagePreview] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    playingAreaName: "",
    isEntireCourtAllowed: false,
    isRequestAllowed: false,
    isRegistrationFeesApplicable: false,
    registrationFees: "",
    renewalFees: "",
    proficiency: [],
    proficiencyLevels: {},
    courtBookingNotes: "",
    sportsImage: null,
    heroImage: null,
  });

  const [proficiencyOptions, setProficiencyOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_ADMIN_SPORT_NEW, {
          method: "GET",
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          const proficiencyList = data.data.allProficiencies.map(proficiency => ({
            value: proficiency,
            label: proficiency,
          }));
          setProficiencyOptions(proficiencyList);
        }
      } catch (error) {
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          if (name === "sportsImage") {
            setFormData(prevState => ({
              ...prevState,
              sportsImage: fileReader.result,
              sportsImageName: file.name
            }));
          } else if (name === "heroImage") { 
            setFormData(prevState => ({
              ...prevState,
              heroImage: fileReader.result,
              heroImageName: file.name
            }));
          }
        };
        fileReader.readAsDataURL(file);
      }
    } else {
      const inputValue = type === 'checkbox' ? checked : value;
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: inputValue,
      }));
    }
  };

  const handleSelectChange = (selectedOptions) => {
    const selectedProficiencies = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prevState => ({
      ...prevState,
      proficiency: selectedProficiencies,
      proficiencyLevels: Object.fromEntries(
        Object.entries(prevState.proficiencyLevels)
          .filter(([key]) => selectedProficiencies.includes(key))
      ),
    }));
  };
  useEffect(() => {
    const fetchSportData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_EDIT_SPORT_DETAILS}/${sportId}`, 
          {
            headers: { 'Authorization': `Bearer ${token}` },
          }
        );
  
        const responseData = response.data;
  
        if (responseData.success && responseData.data.sportEditFrom) {
          const sportData = responseData.data.sportEditFrom;
          const selectedProficiencies = sportData.proficiencies.map(prof => ({
            value: prof,
            label: prof
          }));
  
          setFormData({
            id: sportData.id || "",
            name: sportData.name || "",
            description: sportData.description || "",
            playingAreaName: sportData.playingAreaName || "",
            isEntireCourtAllowed: !!sportData.allowEntireCourtBooking,
            isRequestAllowed: !!sportData.requestAllowed,
            isRegistrationFeesApplicable: !!sportData.isRegistrationFees,
            proficiency: sportData.proficiencies,
            proficiencyLevels: Object.entries(sportData.proficiency || {}).reduce((acc, [key, levels]) => {
              acc[key] = levels.join(', ');
              return acc;
            }, {}),
            registrationFees: sportData.registrationFees || "",
            renewalFees: sportData.renewalFees || "",
            courtBookingNotes: sportData.courtBookingNotes || "",
          });
          setImagePreview(sportData.fileUrl || null);
          setHeroImagePreview(sportData.heroImageUrl || null);
        }
      } catch (error) {
        console.error("Error fetching sport data:", error);
      }
    };
  
    if (action === "edit" && sportId) {
      fetchSportData();
    }
  }, [action, sportId, token]);
  

  const handleLevelChange = (proficiency, level) => {
    setFormData(prevState => ({
      ...prevState,
      proficiencyLevels: {
        ...prevState.proficiencyLevels,
        [proficiency]: level
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allSublevelsFilled = formData.proficiency.every(prof =>
      formData.proficiencyLevels[prof] && formData.proficiencyLevels[prof].trim() !== ''
    );

    if (!allSublevelsFilled) {
      Swal.fire({
        icon: 'error',
        title: 'Incomplete Sublevels',
        text: 'Please ensure the selected proficiencies have at least one sublevel.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    if (!formData.id && !formData.sportsImage) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Sports image is mandatory when form ID is not present.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const sportForm = {
      id: formData.id || null,
      name: formData.name,
      description: formData.description,
      playingAreaName: formData.playingAreaName,

      proficiency: formData.proficiencyLevels
        ? Object.entries(formData.proficiencyLevels).reduce((acc, [key, value]) => {
          acc[key.toUpperCase()] = value
            .split(",")
            .map(level => level.trim())
            .filter(level => level !== "");
          return acc;
        }, {})
        : {},

      allowEntireCourtBooking: formData.isEntireCourtAllowed === "on" ? true : !!formData.isEntireCourtAllowed,
      courtBookingNotes: formData.courtBookingNotes,
      requestAllowed: formData.isRequestAllowed === "on" ? true : !!formData.isRequestAllowed,
      isRegistrationFees: formData.isRegistrationFeesApplicable === "on" ? true : !!formData.isRegistrationFeesApplicable,
      registrationFees: Number(formData.registrationFees) || 0,
      renewalFees: Number(formData.renewalFees) || 0,
    };

    const formDataToSend = new FormData();
    formDataToSend.append("sportForm", new Blob([JSON.stringify(sportForm)], { type: "application/json" }));

    if (formData.sportsImage && formData.sportsImage.startsWith("data:image")) {
      const blob = await fetch(formData.sportsImage).then((res) => res.blob());
      const file = new File([blob], formData.sportsImageName || "uploaded_image.jpeg", { type: blob.type });
      formDataToSend.append("file", file);
    }

    if (formData.heroImage && formData.heroImage.startsWith("data:image")) {
      const blob = await fetch(formData.heroImage).then((res) => res.blob());
      const file = new File([blob], formData.heroImageName || "hero_image.jpeg", { type: blob.type });
      formDataToSend.append("heroFile", file); 
    }

    try {
      const apiUrlBase = process.env.REACT_APP_API_ADMIN_SPORT_NEW_ADD;
      const apiUrl = formData.id ? `${apiUrlBase}?sportId=${formData.id}` : apiUrlBase;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const responseText = await response.text();

      if (!responseText.trim()) {
        navigate("/sportList");
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: formData.id ? 'Sport updated successfully!' : 'Sport submitted successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        return;
      }

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: formData.id ? 'Sport updated successfully!' : 'Sport submitted successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        navigate("/sportList");
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.success_message || "No message provided",
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/sportList" className="breadcrumb-link">Sport List</Link> &gt; New Sport
      </div>
      <form className="walkin-enquiry-form" onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${formData.name ? 'has-value' : ''}`}
            value={formData.name || ''}
            onChange={handleChange}
            placeholder="Enter Name"
            required
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="description"
            className={`walkin-form ${formData.description ? 'has-value' : ''}`}
            value={formData.description || ''}
            onChange={handleChange}
            placeholder="Enter Description"
            required
          />
          {formData.description && <label htmlFor="description">Description</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="playingAreaName"
            className={`walkin-form ${formData.playingAreaName ? 'has-value' : ''}`}
            value={formData.playingAreaName || ''}
            onChange={handleChange}
            placeholder="e.g., Court, Pool, Turf"
            required
          />
          {formData.playingAreaName && (
            <label htmlFor="playingAreaName">Playing Area Name</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="registrationFees"
            className={`walkin-form ${formData.registrationFees ? 'has-value' : ''}`}
            value={formData.registrationFees || ''}
            onChange={handleChange}
            placeholder="Enter Registration Fees"
            required
          />
          {formData.registrationFees && (
            <label htmlFor="registrationFees">Registration Fees</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="renewalFees"
            className={`walkin-form ${formData.renewalFees ? 'has-value' : ''}`}
            value={formData.renewalFees || ''}
            onChange={handleChange}
            placeholder="Enter Renewal Fees"
          />
          {formData.renewalFees && (
            <label htmlFor="renewalFees">Renewal Fees</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="proficiency"
            isMulti
            options={proficiencyOptions}
            onChange={handleSelectChange}
            value={proficiencyOptions.filter(option =>
              formData.proficiency.includes(option.value)
            )}
            className={`walkin-form-dropdown ${formData.proficiency.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Proficiency"
            isClearable
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {formData.proficiency.length > 0 && (
            <label htmlFor="proficiency">Proficiency</label>
          )}
        </div>

        {formData.proficiency.map(prof => (
          <div className="floating-select-form" style={{ width: '98%' }} key={prof}>
            <div className="input-container" style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                name={`${prof}-sublevel`}
                className={`walkin-form ${formData.proficiencyLevels[prof] ? 'has-value' : ''}`}
                value={formData.proficiencyLevels[prof] || ''}
                onChange={(e) => handleLevelChange(prof, e.target.value)}
                placeholder={`Enter ${prof} Sublevel`}
              />
              {formData.proficiencyLevels[prof] && (
                <label htmlFor={`${prof}-sublevel`}>{prof} Sublevel</label>
              )}
              <button
                type="button"
                className="add-button"
                style={{ marginLeft: '8px' }}
                onClick={() => {
                  setFormData(prevState => ({
                    ...prevState,
                    proficiencyLevels: {
                      ...prevState.proficiencyLevels,
                      [prof]: prevState.proficiencyLevels[prof]
                        ? `${prevState.proficiencyLevels[prof]}, `
                        : ""
                    }
                  }));
                }}
              >
                +
              </button>
            </div>

          </div>

        ))}

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <textarea
            name="courtBookingNotes"
            className={`walkin-form-comments ${formData.courtBookingNotes ? 'has-value' : ''}`}
            value={formData.courtBookingNotes || ''}
            onChange={handleChange}
            placeholder="Use # to add new point. To highlight a text, wrap it in <highlight></highlight> tag. Ex: <highlight>TEXT</highlight>"
            rows="5"
            required
            style={{ resize: 'vertical' }}
          />
          {formData.courtBookingNotes && (
            <label htmlFor="courtBookingNotes">Court Booking Notes</label>
          )}
        </div>
        <fieldset className="enrollment-form-fieldset" style={{ marginTop: '0px' }}>
          <legend>Additional Options:</legend>
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="isEntireCourtAllowed"
                className="form-checkbox"
                checked={formData.isEntireCourtAllowed}
                onChange={handleChange}
              />
              Is Entire Court Booking Allowed?
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="isRequestAllowed"
                className="form-checkbox"
                checked={formData.isRequestAllowed}
                onChange={handleChange}
              />
              Is Request Allowed For Sport?
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="isRegistrationFeesApplicable"
                className="form-checkbox"
                checked={formData.isRegistrationFeesApplicable || false}
                onChange={handleChange}
              />
              Is Registration Fees Applicable?
            </label>
          </div>
        </fieldset>
        <div className="enrollment-form-section" style={{ marginBottom: '0px' }}>
          <label className="form-label">Hero Image:</label>
          {heroImagePreview && !formData.heroImage && (
            <div className="image-preview">
              <img
                src={heroImagePreview}
                alt="Hero Image Preview"
                className="hero-image"
                style={{ maxWidth: '50%', height: 'auto' }}
              />
            </div>
          )}
          
          {formData.heroImage && (
            <div className="image-preview">
              <img
                src={formData.heroImage}
                alt="Current Hero Image"
                className="hero-image"
                style={{ maxWidth: '50%', height: 'auto' }}
              />
            </div>
          )}

          <input
            type="file"
            name="heroImage"
            className="form-file"
            accept="image/*"
            onChange={handleChange}
            id="uploadHeroInput"
            style={{ display: 'none' }}
          />

          <button
            type="button"
            className="uploadbutton"
            onClick={() => document.getElementById('uploadHeroInput').click()}
          >
            <span className="uploadbutton__icon">
              <svg strokeLinejoin="round" strokeLinecap="round" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <path fill="none" d="M0 0h24v24H0z" stroke="none"></path>
                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
                <path d="M9 15l3 -3l3 3"></path>
                <path d="M12 12l0 9"></path>
              </svg>
            </span>
            <span className="uploadbutton__text">Upload Hero Image</span>
          </button>
        </div>

        <div className="enrollment-form-section" style={{ marginBottom: '0px' }}>
          <label className="form-label">Sports Icon:</label>
          {imagePreview && !formData.sportsImage && (
            <div className="image-preview">
              <img
                src={imagePreview}
                alt="Sport Image Preview"
                className="sport-image"
                style={{ maxWidth: '50%', height: 'auto' }}
              />
            </div>
          )}

          {formData.sportsImage && (
            <div className="image-preview">
              <img
                src={formData.sportsImage}
                alt="Current Sport Image"
                className="sport-image"
                style={{ maxWidth: '50%', height: 'auto' }}
              />
            </div>
          )}

          <input
            type="file"
            name="sportsImage"
            className="form-file"
            accept="image/*"
            onChange={handleChange}
            id="uploadInput"
            style={{ display: 'none' }}
          />

          <button
            type="button"
            className="uploadbutton"
            onClick={() => document.getElementById('uploadInput').click()}
          >
            <span className="uploadbutton__icon">
              <svg strokeLinejoin="round" strokeLinecap="round" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <path fill="none" d="M0 0h24v24H0z" stroke="none"></path>
                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
                <path d="M9 15l3 -3l3 3"></path>
                <path d="M12 12l0 9"></path>
              </svg>
            </span>
            <span className="uploadbutton__text">Upload Sport Icon</span>
          </button>
        </div>

        <div>
          <button type="submit" className="submit-button">
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewSport;
